import { CryptoEvent } from 'matrix-js-sdk/lib/crypto-api';
import { updateEvent } from '../../reducer/matrix';
import { createTEvent } from './event';
const SWEEP_DEBOUNCE_MS = 500;
const isFailedEncryptedEvent = (evt) => {
    const encrypted = (typeof evt.isEncrypted === 'function' && evt.isEncrypted()) ||
        (typeof evt.getType === 'function' && evt.getType() === 'm.room.encrypted');
    if (!encrypted)
        return false;
    const failed = typeof evt.isDecryptionFailure === 'function' && evt.isDecryptionFailure();
    const placeholder = evt.getContent?.()?.msgtype === 'm.bad.encrypted';
    return failed || placeholder;
};
/**
 * Install a listener that re-decrypts every still-failed encrypted event in
 * every room whenever backup-imported keys become available, and pushes any
 * newly-decryptable payload into Redux.
 *
 * Returns an unsubscribe function that detaches all listeners and cancels
 * any pending sweep timer. Safe to call multiple times.
 */
export const installBackupKeyListener = (client, dispatch) => {
    const cryptoClient = client;
    let sweepInFlight = false;
    let sweepPending = false;
    let sweepTimer = null;
    let detached = false;
    const sweepRoomsForFailedDecryption = async () => {
        if (detached)
            return;
        if (sweepInFlight) {
            sweepPending = true;
            return;
        }
        sweepInFlight = true;
        try {
            const rooms = client.getRooms();
            for (const room of rooms) {
                const events = room.getLiveTimeline().getEvents();
                for (const evt of events) {
                    if (!isFailedEncryptedEvent(evt))
                        continue;
                    try {
                        await client.decryptEventIfNeeded(evt, { isRetry: true });
                    }
                    catch {
                        continue;
                    }
                    // Skip dispatch if the retry did not actually produce cleartext.
                    if (isFailedEncryptedEvent(evt))
                        continue;
                    try {
                        const te = await createTEvent(evt);
                        if (te) {
                            dispatch(updateEvent({ event: te, roomId: room.roomId }));
                        }
                    }
                    catch (e) {
                        console.warn('[BackupKeyListener] createTEvent failed', e);
                    }
                }
            }
        }
        finally {
            sweepInFlight = false;
            if (sweepPending && !detached) {
                sweepPending = false;
                // Defer to next microtask so a flood of triggers cannot starve
                // the event loop with synchronous re-runs.
                void Promise.resolve().then(() => sweepRoomsForFailedDecryption());
            }
        }
    };
    const scheduleSweep = () => {
        if (detached)
            return;
        if (sweepTimer)
            clearTimeout(sweepTimer);
        sweepTimer = setTimeout(() => {
            sweepTimer = null;
            void sweepRoomsForFailedDecryption();
        }, SWEEP_DEBOUNCE_MS);
    };
    // Fired repeatedly while a backup download is in progress; the count
    // typically goes from N -> 0. Debounced to one sweep per burst.
    const onSessionsRemaining = (..._args) => {
        scheduleSweep();
    };
    // Fired the moment SSSS hands over the backup decryption key. By the
    // time this fires, rust-crypto is about to start importing sessions,
    // so a slightly-deferred sweep will catch them.
    const onDecryptionKeyCached = (..._args) => {
        scheduleSweep();
    };
    // Fired on enable/disable transitions of server-side backup. When the
    // backup becomes enabled we want one sweep to catch any sessions that
    // arrived alongside the enable transition.
    const onBackupStatus = (...args) => {
        const enabled = args[0] === true;
        if (enabled)
            scheduleSweep();
    };
    cryptoClient.on(CryptoEvent.KeyBackupSessionsRemaining, onSessionsRemaining);
    cryptoClient.on(CryptoEvent.KeyBackupDecryptionKeyCached, onDecryptionKeyCached);
    cryptoClient.on(CryptoEvent.KeyBackupStatus, onBackupStatus);
    return () => {
        if (detached)
            return;
        detached = true;
        if (sweepTimer) {
            clearTimeout(sweepTimer);
            sweepTimer = null;
        }
        cryptoClient.removeListener(CryptoEvent.KeyBackupSessionsRemaining, onSessionsRemaining);
        cryptoClient.removeListener(CryptoEvent.KeyBackupDecryptionKeyCached, onDecryptionKeyCached);
        cryptoClient.removeListener(CryptoEvent.KeyBackupStatus, onBackupStatus);
    };
};
